import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

// Import your images
import factory1 from "../assets/factory1.jpeg";
import factory2 from "../assets/factory2.jpeg";
import factory3 from "../assets/factory3.jpeg";
import factory4 from "../assets/factory4.jpeg";

// partner images
import dhassoc from "../assets/d&h.png";
import esab from "../assets/esab.png";
import norton from "../assets/norton.png";
import mangalam from "../assets/mangalam.jpeg";

// Import product & feature icons
import oxygenIcon from "../assets/oxygen-icon.png";
import nitrogenIcon from "../assets/nitrogen-icon.png";
import hydrogenIcon from "../assets/hydrogen-icon.png";
import argonIcon from "../assets/argon-icon.png";

// Import pattern images
import particlePattern from "../assets/particle-pattern.png";
import gridPattern from "../assets/grid-pattern.png";

// Import product category icons
import weldingIcon from "../assets/welding-icon.jpeg";
import cylinderIcon from "../assets/cylinder-icon.jpeg";
import ropeIcon from "../assets/rope-icon.jpeg";
import ppeIcon from "../assets/ppe-icon.jpeg";
import safetyIcon from "../assets/safety-icon.jpeg";

// Import client logos
import tataLogo from "../assets/clients/tata.png";
import bhelLogo from "../assets/clients/iffco.png";
import iocLogo from "../assets/clients/kec.png";
import ntpcLogo from "../assets/clients/ntpc.png";
import hpclLogo from "../assets/clients/adani.png";
import jswLogo from "../assets/clients/gmr.png";
import vedantaLogo from "../assets/clients/thermax.png";
import ltLogo from "../assets/clients/lt.png";

function Home() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const images = [factory2, factory3, factory1, factory4];

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isHovered) {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
      }
    }, 15000); // Increased duration for better user experience
    return () => clearInterval(interval);
  }, [images.length, isHovered]);

  const productCategories = [
    {
      title: "Welding Materials",
      items: [
        "Welding electrodes",
        "Filler wires",
        "Welding machines",
        "Welding holders",
        "Welding copper cables",
        "Welding aluminium cables",
        "Welding goggles",
        "Welding helmets",
        "Welding gloves",
        "Welding hand screens",
        "Welding wires",
        "Welding cable connector ",
        "Copper lugs",
        "Aluminium lugs",
        "Copper rods",
        "D.P sets",
        "Electrode dying oven",
        "Electrode drying oven",
        "Welding glass",
        "Welding torch`",
        "Hand tools",
        "Power tools",
        "Etc.",
      ]
    },
    {
      title: "Industrial Gas Cylinders",
      items: [
        "Oxygen Cylinders",
        "Nitrogen Cylinders",
        "Hydrogen Cylinders",
        "Argon Cylinders",
        "Carbon Dioxide Cylinders",
        "Dissolved Acetylene Cylinders",
        "Gas Cylinders",
        "Etc.",
      ]
    },
    {
      title: "Ropes, Clamps & Web-slings",
      items: [
        "PP ropes",
        "U clamps",
        "D shackle",
        "Chain pully block",
        "Wire rope slings",
        "Web slings",
        "Etc.",
      ]
    },
    {
      title: "Personal Protective Equipment",
      items: [
        "Head protection",
        "Eye protection",
        "Hand protection",
        "Foot protection",
        "Safety harness",
        "Etc.", 
      ]
    },
    {
      title: "Road & Fire Safety",
      items: [
        "Fire extinguishers",
        "Speed breakers",
        "Fire safety equipment",
        "Emergency response kits",
        "Radium tapes",
        "Safety cones",
        "Safety jackets",
        "Fire blankets",
        "Bucket with stands",
        "Etc.",
      ]
    },
  ];

  return (
    <div className="bg-white pt-[160px]">
      {/* Enhanced Hero Section with Modern Design */}
      <section 
        className="relative min-h-[calc(100vh-160px)] bg-gray-900 overflow-hidden"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Dynamic Background Image with Enhanced Transition */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentImageIndex}
            initial={{ opacity: 0, scale: 1.2 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.1 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: `url(${images[currentImageIndex]})`,
              filter: 'brightness(0.8) contrast(1.2) saturate(1.3)'
            }}
          />
        </AnimatePresence>

        {/* Enhanced Gradient Overlays */}
        {/* <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#D82F2F]/30 via-transparent to-[#D82F2F]/30" /> */}
        
        {/* Dynamic Animated Shapes */}
        <motion.div
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.2, 0.1],
            rotate: [0, 90, 0]
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-1/4 left-1/4 w-[45rem] h-[45rem] bg-gradient-to-br from-[#D82F2F]/20 to-transparent rounded-full blur-3xl"
        />
        <motion.div
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.1, 0.2, 0.1],
            rotate: [90, 0, 90]
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 5
          }}
          className="absolute bottom-1/4 right-1/4 w-[45rem] h-[45rem] bg-gradient-to-bl from-[#D82F2F]/20 to-transparent rounded-full blur-3xl"
        />

        {/* Enhanced Hero Content */}
        <div className="relative container mx-auto px-4 h-screen">
          <div className="h-full flex flex-col justify-center items-center text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="relative max-w-4xl"
            >
              {/* Animated Decorative Elements */}
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: "100%" }}
                transition={{ duration: 1.5, delay: 0.5 }}
                className="absolute -top-12 left-1/2 transform -translate-x-1/2 h-1 bg-gradient-to-r from-transparent via-[#D82F2F] to-transparent"
              />
              
              {/* Enhanced Typography */}
              <div className="relative">
                <motion.span
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  className="block text-3xl md:text-4xl font-bold mb-6 text-gray-200 tracking-wider"
                >
                  Welcome to
                </motion.span>
                <motion.h1
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  className="text-5xl md:text-7xl font-bold text-red-500 mb-8 tracking-tight drop-shadow-2xl"
                >
                  <span className="relative inline-block">
                    Triveni Industrial Gases
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: "100%" }}
                      transition={{ duration: 1.2, delay: 1 }}
                      className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-[#D82F2F] via-white to-[#D82F2F]"
                    />
                  </span>
                </motion.h1>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.4 }}
              className="space-y-10 relative"
            >
              {/* Enhanced Tagline */}
              <div className="relative">
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1, delay: 0.8 }}
                  className="text-2xl md:text-4xl text-white font-light italic tracking-wide drop-shadow-2xl"
                >
                  Strong Welds, Stronger Bonds
                </motion.p>
                
                {/* Animated Decorative Elements */}
                <div className="flex items-center justify-center gap-6 mt-8">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: "6rem" }}
                    transition={{ duration: 1, delay: 1 }}
                    className="h-0.5 bg-gradient-to-r from-transparent to-[#D82F2F]"
                  />
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ duration: 0.8, delay: 1.2 }}
                    className="w-4 h-4 rounded-full bg-[#D82F2F] shadow-[0_0_15px_rgba(216,47,47,0.5)]"
                  />
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: "6rem" }}
                    transition={{ duration: 1, delay: 1 }}
                    className="h-0.5 bg-gradient-to-l from-transparent to-[#D82F2F]"
                  />
                </div>
              </div>

              {/* Enhanced CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 1.4 }}
                className="pt-8 flex flex-col md:flex-row items-center justify-center gap-6"
              >
                <Link
                  to="/contact"
                  className="group relative inline-flex items-center px-8 py-4 text-lg font-medium text-white bg-[#D82F2F] rounded-full overflow-hidden transition-all duration-500 hover:scale-105"
                >
                  <span className="relative z-10 flex items-center">
                    Get Started
                    <motion.svg 
                      className="w-6 h-6 ml-2 transition-transform duration-300 group-hover:translate-x-1" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </motion.svg>
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-[#FF4D4D] to-[#D82F2F] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </Link>
                <Link
                  to="/products/welding-materials"
                  className="group relative inline-flex items-center px-8 py-4 text-lg font-medium text-white overflow-hidden transition-all duration-500 hover:scale-105"
                >
                  <span className="relative z-10 flex items-center">
                    Explore Products
                    <motion.svg 
                      className="w-6 h-6 ml-2 transition-transform duration-300 group-hover:translate-x-1" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </motion.svg>
                  </span>
                  <div className="absolute inset-0 border-2 border-white/40 rounded-full group-hover:border-[#D82F2F] transition-colors duration-500" />
                  <div className="absolute inset-0 bg-white/10 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>


        {/* Enhanced Floating Particles */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(30)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-white/30 rounded-full"
              initial={{
                x: Math.random() * 100 + "%",
                y: Math.random() * 100 + "%",
                scale: Math.random() * 0.5 + 0.5,
                opacity: Math.random() * 0.3 + 0.2
              }}
              animate={{
                y: [null, "-100%"],
                opacity: [null, 0]
              }}
              transition={{
                duration: Math.random() * 10 + 15,
                repeat: Infinity,
                ease: "linear",
                delay: Math.random() * 10
              }}
            />
          ))}
        </div>
      </section>

      {/* Authorized Partners Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        {/* Background Decorative Elements */}
        <motion.div
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.03, 0.08, 0.03]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-0 right-0 w-[30rem] h-[30rem] bg-[#D82F2F]/5 rounded-full blur-3xl"
        />

        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#333333] mb-6">
              Our Trusted Partners
            </h2>
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="w-16 h-0.5 bg-gradient-to-r from-transparent to-[#D82F2F]" />
              <div className="w-2 h-2 rounded-full bg-[#D82F2F]" />
              <div className="w-16 h-0.5 bg-gradient-to-l from-transparent to-[#D82F2F]" />
            </div>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Authorized distributor and channel partner of leading industrial brands
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {[
              { img: esab, name: "ESAB", delay: 0 },
              { img: dhassoc, name: "D&H Associates", delay: 0.1 },
              { img: mangalam, name: "Mangalam", delay: 0.1 },
              { img: norton, name: "Norton", delay: 0.2 },
              { name: "Rajdeep Electrodes", delay: 0.3, isText: true }
            ].map((partner, index) => (
              <motion.div
                key={partner.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: partner.delay }}
                className="group"
              >
                <div className="relative bg-white rounded-2xl p-10 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
                  {/* Gradient Border */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#D82F2F]/15 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300" />
                  
                  {/* Partner Logo or Text */}
                  <div className="relative flex items-center justify-center h-32">
                    {partner.isText ? (
                      <motion.h3
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.3 }}
                        className="text-3xl font-bold text-[#D82F2F] text-center group-hover:text-[#333333] transition-colors duration-300"
                      >
                        {partner.name}
                      </motion.h3>
                    ) : (
                      <motion.img 
                        src={partner.img} 
                        alt={partner.name}
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.3 }}
                        className="max-h-32 w-auto object-contain transition-all duration-300 group-hover:brightness-110" 
                      />
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Categories Section */}
      <section className="py-20 bg-gradient-to-b from-white via-gray-50/50 to-white relative overflow-hidden">
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-[#333333] mb-6">
              Our Product Categories
            </h2>
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="w-16 h-0.5 bg-gradient-to-r from-transparent to-[#D82F2F]" />
              <div className="w-2 h-2 rounded-full bg-[#D82F2F]" />
              <div className="w-16 h-0.5 bg-gradient-to-l from-transparent to-[#D82F2F]" />
            </div>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Discover our comprehensive range of industrial products and equipment solutions
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
            {productCategories.map((category, idx) => (
              <motion.div
                key={category.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="group bg-white rounded-3xl p-10 shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <Link 
                  to={
                    idx === 0 ? "/products/welding-materials" :
                    idx === 1 ? "/products/gas-cylinders" :
                    idx === 2 ? "/products/ropes-clamps" :
                    idx === 3 ? "/products/ppe" :
                    "/products/safety"
                  }
                  className="block"
                >
                  <div className="mb-16">
                    <div className="w-72 h-72 mx-auto rounded-3xl  flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <img 
                        src={
                          idx === 0 ? weldingIcon :
                          idx === 1 ? cylinderIcon :
                          idx === 2 ? ropeIcon :
                          idx === 3 ? ppeIcon :
                          safetyIcon
                        }
                        alt={category.title}
                        className="w-72 h-72 object-contain  transform group-hover:rotate-3 transition-all duration-300"
                      />
                    </div>
                  </div>

                  <h3 className="text-2xl font-bold text-[#333333] mb-6 text-center">
                    {category.title}
                  </h3>

                  <div className="space-y-3">
                    {category.items.slice(0, 6).map((item, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.3, delay: (idx * 0.1) + (i * 0.05) }}
                        className="flex items-center gap-3 hover:bg-gray-50 p-2 rounded-lg transition-all duration-300"
                      >
                        <div className="w-2 h-2 rounded-full bg-[#D82F2F]/40" />
                        <p className="text-gray-600">
                          {item.replace(" I", "")}
                        </p>
                      </motion.div>
                    ))}
                    {category.items.length > 6 && (
                      <div className="text-center pt-2">
                        <span className="text-sm text-gray-500 italic">
                          +{category.items.length - 6} more items
                        </span>
                      </div>
                    )}
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      

      {/* Our Valuable Clients Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 via-white to-gray-50 relative overflow-hidden">
        {/* Decorative Background Elements */}
        <motion.div
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.03, 0.08, 0.03]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-0 right-0 w-[40rem] h-[40rem] bg-[#D82F2F]/5 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ 
            scale: [1.1, 1, 1.1],
            opacity: [0.03, 0.08, 0.03]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 5
          }}
          className="absolute bottom-0 left-0 w-[30rem] h-[30rem] bg-[#D82F2F]/5 rounded-full blur-3xl"
        />

        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-[#333333] mb-6">
              Our Valuable Clients
            </h2>
            <div className="flex items-center justify-center gap-4 mb-4">
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: "4rem" }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="h-0.5 bg-gradient-to-r from-transparent to-[#D82F2F]"
              />
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="w-3 h-3 rounded-full bg-[#D82F2F] shadow-[0_0_12px_rgba(216,47,47,0.3)]"
              />
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: "4rem" }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="h-0.5 bg-gradient-to-l from-transparent to-[#D82F2F]"
              />
            </div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-gray-600 text-lg max-w-2xl mx-auto"
            >
              Trusted by leading industries and organizations across India
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {[
              { logo: bhelLogo, name: "IFFCO India", type: "Power Generation" },
              { logo: iocLogo, name: "KEC International", type: "Oil & Gas" },
              { logo: ntpcLogo, name: "NTPC Limited", type: "Power Generation" },
              { logo: hpclLogo, name: "Adani Limited", type: "Oil & Gas" },
              { logo: jswLogo, name: "GMR", type: "Steel Manufacturing" },
              { logo: vedantaLogo, name: "Thermax Limited", type: "Mining" },
              { logo: ltLogo, name: "L&T", type: "Engineering" }
            ].map((client, idx) => (
              <motion.div
                key={client.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="group relative"
              >
                <div className="relative bg-white rounded-2xl p-10 shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden">
                  {/* Gradient Border Effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#D82F2F]/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500" />
                  
                  {/* Client Logo Container */}
                  <motion.div 
                    initial={{ y: 20 }}
                    whileInView={{ y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: idx * 0.3 }}
                    className="mb-8 relative"
                  >
                    <div className="w-full h-40 flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                      <img 
                        src={client.logo} 
                        alt={client.name}
                        className="max-h-32 max-w-[90%] object-contain filter grayscale group-hover:grayscale-0 transition-all duration-500" 
                      />
                    </div>
                    {/* Decorative Elements */}
                    <div className="absolute -top-6 -right-6 w-16 h-16 bg-[#D82F2F]/5 rounded-full blur-xl group-hover:scale-150 transition-transform duration-500" />
                  </motion.div>

                  {/* Client Info */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: idx * 0.4 }}
                    className="text-center relative"
                  >
                    <h3 className="text-xl font-bold text-[#333333] mb-2 group-hover:text-[#D82F2F] transition-colors duration-300">
                      {client.name}
                      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-0.5 bg-gradient-to-r from-transparent via-[#D82F2F]/40 to-transparent group-hover:w-32 transition-all duration-500" />
                    </h3>
                    <p className="text-sm text-gray-600">
                      {client.type}
                    </p>
                  </motion.div>

                  {/* Hover Corner Effects */}
                  <div className="absolute top-2 left-2 w-6 h-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute top-0 left-0 w-full h-0.5 bg-[#D82F2F]/30" />
                    <div className="absolute top-0 left-0 w-0.5 h-full bg-[#D82F2F]/30" />
                  </div>
                  <div className="absolute bottom-2 right-2 w-6 h-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 right-0 w-full h-0.5 bg-[#D82F2F]/30" />
                    <div className="absolute bottom-0 right-0 w-0.5 h-full bg-[#D82F2F]/30" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Enhanced Trust Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-16 text-center"
          >
            <div className="inline-flex items-center px-8 py-4 bg-white/80 backdrop-blur-sm rounded-full shadow-lg">
              <div className="w-14 h-14 bg-[#D82F2F]/10 rounded-full flex items-center justify-center mr-4">
                <svg className="w-7 h-7 text-[#D82F2F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div className="text-left">
                <h4 className="font-semibold text-[#333333]">Trusted Excellence</h4>
                <p className="text-sm text-gray-600">Serving 100+ Industrial Clients Across India</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Enhanced Contact Section */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50/50 relative overflow-hidden">
        {/* Decorative Background Elements */}
        <motion.div
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.03, 0.08, 0.03]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-0 right-0 w-[40rem] h-[40rem] bg-[#D82F2F]/5 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ 
            scale: [1.1, 1, 1.1],
            opacity: [0.03, 0.08, 0.03]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 5
          }}
          className="absolute bottom-0 left-0 w-[30rem] h-[30rem] bg-[#D82F2F]/5 rounded-full blur-3xl"
        />

        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-[#333333] mb-6">
              Let's Connect
            </h2>
            <div className="flex items-center justify-center gap-4 mb-4">
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: "4rem" }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="h-0.5 bg-gradient-to-r from-transparent to-[#D82F2F]"
              />
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="w-3 h-3 rounded-full bg-[#D82F2F] shadow-[0_0_12px_rgba(216,47,47,0.3)]"
              />
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: "4rem" }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="h-0.5 bg-gradient-to-l from-transparent to-[#D82F2F]"
              />
            </div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-gray-600 text-lg max-w-2xl mx-auto"
            >
              Reach out to us for any inquiries or support
            </motion.p>
          </motion.div>

          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12">
            {/* Enhanced Contact Info Card */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 relative overflow-hidden group"
            >
              {/* Decorative Elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#D82F2F]/10 to-transparent rounded-bl-full transform translate-x-8 -translate-y-8" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-[#D82F2F]/10 to-transparent rounded-tr-full transform -translate-x-8 translate-y-8" />
              
              <div className="relative space-y-8">
                <div className="text-center md:text-left">
                  <motion.h3 
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="text-3xl font-bold text-[#333333] mb-4"
                  >
                    Triveni Industrial Gases
                  </motion.h3>
                  <div className="w-20 h-1 bg-gradient-to-r from-[#D82F2F] to-[#FF4D4D] mx-auto md:mx-0 rounded-full" />
                </div>

                <div className="space-y-6">
                  {/* Address Card */}
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="flex items-start gap-8 p-6 rounded-xl hover:bg-gray-50/80 transition-all duration-300"
                  >
                    <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-[#D82F2F] to-[#FF4D4D] rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-500">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-2xl font-semibold text-[#333333] mb-3">Visit Us</h4>
                      <p className="text-gray-600 leading-relaxed text-lg">
                        53/38 Bai Ka Bagh,<br />
                        Prayagraj, Uttar Pradesh
                      </p>
                    </div>
                  </motion.div>

                  {/* Phone Card */}
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="flex items-start gap-8 p-6 rounded-xl hover:bg-gray-50/80 transition-all duration-300"
                  >
                    <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-[#D82F2F] to-[#FF4D4D] rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-500">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-2xl font-semibold text-[#333333] mb-3">Call Us</h4>
                      <p className="text-gray-600 leading-relaxed text-lg">
                        +91 8299204736<br />
                        +91 9119117385
                      </p>
                    </div>
                  </motion.div>

                  {/* Email Card */}
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="flex items-start gap-8 p-6 rounded-xl hover:bg-gray-50/80 transition-all duration-300"
                  >
                    <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-[#D82F2F] to-[#FF4D4D] rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-500">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-2xl font-semibold text-[#333333] mb-3">Email Us</h4>
                      <p className="text-gray-600">trivenigases10@gmail.com</p>
                    </div>
                  </motion.div>
                </div>

                {/* Enhanced CTA Button */}
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="mt-8"
                >
                  <a 
                    href="https://maps.app.goo.gl/TuDG7YpLrKsaGEWSA?g_st=iwb"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-[#D82F2F] to-[#FF4D4D] text-white rounded-full hover:shadow-lg hover:shadow-[#D82F2F]/20 transition-all duration-300"
                  >
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                    Get Directions
                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </a>
                </motion.div>
              </div>
            </motion.div>

            {/* Enhanced Map Card */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative group"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#D82F2F] to-[#FF4D4D] rounded-3xl blur-xl opacity-20 group-hover:opacity-30 transition-opacity duration-500" />
              <div className="relative bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500">
                <div className="absolute inset-0 bg-gradient-to-br from-[#D82F2F]/10 via-transparent to-[#D82F2F]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3602.3339644983086!2d81.8535145!3d25.4463099!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x399acac93c62c83d%3A0x36cc4292fc0f7d0f!2sTriveni%20Gas!5e0!3m2!1sen!2sin!4v1709900000000!5m2!1sen!2sin"
                  width="100%"
                  height="600"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="rounded-3xl"
                />
              </div>
            </motion.div>
          </div>

          {/* Enhanced Trust Indicators */}
          {/* <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-16 flex flex-wrap justify-center gap-8"
          >
            {[
              { icon: "🏢", text: "Serving Pan India" },
              { icon: "⭐", text: "Premium Quality" },
              { icon: "🤝", text: "24/7 Support" },
            ].map((item, idx) => (
              <motion.div
                key={idx}
                whileHover={{ scale: 1.05 }}
                className="flex items-center gap-4 px-8 py-4 bg-white/80 backdrop-blur-sm rounded-full shadow-lg"
              >
                <span className="text-3xl">{item.icon}</span>
                <span className="text-gray-700 font-medium text-lg">{item.text}</span>
              </motion.div>
            ))}
          </motion.div> */}
        </div>
      </section>
    </div>
  );
}

export default Home;
